<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" :title="modalTitle" @show="onShow" @hidden="onHidden"
                   :content-in-form="false" :show-confirmation="showConfirmation" :ok-title="$t('Submit')"
                   :data="model" @ok.prevent="handleSubmit(onSubmit)" scrollable :is-loading="loading"
                   :modal-loading="modalLoading" :disable-submit-btn="modalLoading" modal-class="mobile-full-screen">
      <div class="row mb-3">

        <div v-if="!enableContinuousAuction" class="col-lg-3 col-sm-4 col-xl-2">
          <template v-if="customer && disableCustomerInput">
            <b-form-group>
              <label>{{ $t('Customer') }}</label>
              <b-input :value="`${customer.display_name} - ${customer.email}`" disabled/>
            </b-form-group>
          </template>
          <template v-else>
            <customer-select-input-widget
              :model="model" attribute="customer_uuid" :disabled="disabled" @select="onCustomerChange"
              @unselect="clearCustomerInput" expand="billing_address.country,identification">
            </customer-select-input-widget>
          </template>
        </div>
        <div v-else class="col-lg-3 col-sm-4 col-xl-2">
          <lot-select-input-widget :model="model" attribute="lot_uuid" :disabled="disabled"
                                   :expand="'auction.currency,inventory.customer.billing_address'"
                                   @unselect="clearLotInput" @select="onLotChange">
          </lot-select-input-widget>
        </div>

        <div class="col-lg-3 col-sm-4 col-xl-2" v-if="!enableContinuousAuction && !hideSaleInput">
          <sale-select-input-widget :model="model" attribute="auction_uuid" :disabled="disabled" @select="onSaleChange"
                                    @unselect="clearSaleInput">
          </sale-select-input-widget>
        </div>

        <div class="col-lg-3 col-sm-4 col-xl-2">
          <input-widget :model="model" attribute="date" type="datepicker" :min="$getMonthAgo(model.created_at)"
                        :max="$getMonthCurrent(model.created_at)"
                        :rules="`min_date:${$getMonthAgo(model.created_at)}|max_date:${$getMonthCurrent(model.created_at)}|required`"/>
        </div>

        <div class="col-lg-3 col-sm-4 col-xl-2" v-if="hasMoreThanOneLanguage">
          <language-code-select-input-widget hint :model="model" attribute="preferred_language_code"/>
        </div>
      </div>

      <div class="row">
        <div class="col-md-4">
          <div class="card">
            <div class="card-header d-flex align-items-center p-2">
              <div>
                {{ $t('Customer Info') }}
              </div>
            </div>
            <div class="card-body p-2" style="min-height: 5.3em">
              <div v-if="customer || model.customer">
                <customer-email-address-info :customer="customer ? customer : model.customer"
                                             show-intra-community-vat-number :show-address="false"/>
              </div>
              <no-data-available v-else v-show="true" height="4"/>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card">
            <div class="card-header d-flex align-items-center p-2">
              <div>
                {{ $t('Settlement Billing address') }}
              </div>
              <b-button class="ml-auto p-0" variant="link" v-permission="'change_settlement'"
                        @click="onBillingAddressEditClick">
                <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                {{ $t('Edit') }}
              </b-button>
            </div>
            <div class="card-body p-2" style="min-height: 5.3em">
              <div v-if="model.settlement_billing_address">
                <p v-html="$options.filters.asAddress(model.settlement_billing_address, false)" class="p-0"></p>
              </div>
              <no-data-available v-else v-show="true" height="4"/>
            </div>
          </div>
        </div>
      </div>

      <div class="settlement-wrapper">
        <div class="row mt-4 mb-2">
          <div class="col-md-auto">
            <input-widget :model="model" type="checkbox" is-switch attribute="additional_page"/>
          </div>
          <div class="col-md-auto">
            <input-widget :model="model" type="checkbox" is-switch attribute="is_exported"/>
          </div>
          <template v-if="showSettlementExtraOptions && showSettlementExtraOptions.length > 0">
            <div class="col-md-auto" v-for="input in showSettlementExtraOptions" :key="`settlement-modal-dynamic-checkboxes-${input.key}`">
              <input-widget is-switch :attribute="input.key" :model="model"
                            type="checkbox" :label="$t(input.label)"/>
            </div>
          </template>
        </div>
        <div class="row">
          <div class="col-md-12 d-flex justify-content-between">
            <div class="d-flex align-items-center mb-2">
              <h5 class="mb-0">{{ $t('Unsettled Items') }}</h5>
            </div>
            <b-button size="sm" variant="primary" :disabled="selectedItems.size < 1" class="mb-2"
                      @click="moveSelectedToSettled()">
              <font-awesome-icon icon="arrow-down"/>
              {{ $t('Move To Settled') }}
            </b-button>
          </div>
          <div class="col-md-12">
            <table class="unsettled-items-table info-table table table-sm lots-table">
              <tr>
                <th>
                  <b-form-checkbox style="padding-left: 2.01em"
                                   @change="selectAllRows($event)"
                                   v-model="isSelectedAll"/>
                </th>
                <th class="lot-col">{{ $t('Lot') }}</th>
                <th class="receipt-col">{{ $t('Receipt / Item') }}</th>
                <th class="hammer-price-col">{{ $t('Hammer Price') }}</th>
                <th class="hammer-adjustment-col">{{ $t('Hammer Adjustment') }}</th>
                <th class="commission-col">{{ $t('Commission') }}</th>
                <th class="status-col">{{ $t('Status') }}</th>
                <th class="invoice-col">{{ $t('Invoice') }}</th>
                <th class="reserve-col">{{ $t('Reserve') }}</th>
                <th class="on-hold-col">{{ $t('On Hold') }}</th>
                <th class="actions-col text-center">{{ $t('Actions') }}</th>
              </tr>
              <template v-if="model.unsettled_financial_items.length > 0">
                <tr :key="`unsettled-item-${item.uuid}`" v-for="(item, index) in model.unsettled_financial_items">
                  <td>
                    <b-form-checkbox style="padding-left: 2.01em"
                                     @change="onRowSelect($event, item)"
                                     :checked="selectedItems.has(item)"/>
                  </td>
                  <td>
                    <clickable-lot-no :obj="item" is-financial-item/>
                  </td>
                  <td>
                    <span v-if="item.lot && item.receipt && item.lot.inventory_uuid">
                      <linked-stock-no :stock-no="item.stock_no" :inventory-uuid="item.lot.inventory_uuid"
                                       :receipt-uuid="item.receipt.uuid"/>
                    </span>
                    <span v-else>
                      {{ item.stock_no }}
                    </span>
                  </td>
                  <td>{{ item.hammer_price | asCurrency(currency) }}</td>
                  <td>
                    <div style="width: 100px">
                      <currency-input-widget :currency="currency" size="sm" :model="item" attribute="hammer_adjustment"
                                             :label="false"/>
                    </div>
                  </td>
                  <td>
                    <div style="width: 100px">
                      <currency-input-widget :currency="currency" size="sm" :model="item" attribute="commission"
                                             :label="false"/>
                    </div>
                  </td>
                  <td>
                    <span v-as-status="item.status">{{ item.status | removeUnderscores | toUpperCaseFirst }}</span>
                  </td>
                  <td>
                    <span v-as-status="item.invoice_status">
                      {{ item.invoice_status || $t("N/A") | removeUnderscores | toUpperCaseFirst }}
                    </span>
                  </td>
                  <td>
                    <div v-if="item.reserve">
                      {{ item.reserve | asCurrency(currency) }}
                    </div>
                    <div v-if="item.reserve_type">
                      {{ item.reserve_type | removeUnderscores | toUpperCaseEveryWord }}
                    </div>
                  </td>
                  <td>
                    <input-widget :model="item" attribute="on_hold" type="checkbox" size="lg" :label="false"/>
                  </td>
                  <td class="text-center">
                    <b-tooltip :target="`move-to-settled-btn-${index}`" :title="$t('Move To Settled')"/>
                    <b-button size="sm" variant="primary" :id="`move-to-settled-btn-${index}`"
                              @click="onMoveToSettledClick(item)">
                      <font-awesome-icon icon="arrow-down"/>
                    </b-button>
                  </td>
                </tr>
              </template>
              <tr v-else>
                <td colspan="10">
                  <no-data-available :height="3"/>
                </td>
              </tr>
            </table>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12 d-flex justify-content-between">
            <div class="d-flex align-items-center mb-2">
              <h5 class="mb-0">{{ $t('Settled Items') }}</h5>
            </div>
            <b-button size="sm" variant="primary" :disabled="selectedSettledItems.size < 1" class="mb-2"
                      @click="moveSelectedToUnsettled()">
              <font-awesome-icon icon="arrow-up"/>
              {{ $t('Move To Unsettled') }}
            </b-button>
          </div>
          <div class="col-md-12">
            <table class="settled-items-table info-table table table-sm lots-table">
              <tr>
                <th>
                  <b-form-checkbox style="padding-left: 2.01em"
                                   @change="selectAllSettledRows($event)"
                                   v-model="isAllSettledSelected"/>
                </th>
                <th class="lot-col">{{ $t('Lot') }}</th>
                <th class="receipt-col">{{ $t('Receipt / Item') }}</th>
                <th class="hammer-price-col">{{ $t('Hammer Price') }}</th>
                <th class="hammer-adjustment-col">{{ $t('Hammer Adjustment') }}</th>
                <th class="commission-col">{{ $t('Commission') }}</th>
                <th class="status-col">{{ $t('Status') }}</th>
                <th class="invoice-col">{{ $t('Invoice') }}</th>
                <th class="reserve-col">{{ $t('Reserve') }}</th>
                <th class="on-hold-col">{{ $t('On Hold') }}</th>
                <th class="actions-col text-center">{{ $t('Actions') }}</th>
              </tr>
              <template v-if="model.financial_items.length > 0">
                <tr :key="`settled-item-${item.uuid}`" v-for="(item, index) in model.financial_items">
                  <td>
                    <b-form-checkbox style="padding-left: 2.01em"
                                     @change="onSettledRowSelect($event, item)"
                                     :checked="selectedSettledItems.has(item)"/>
                  </td>
                  <td>
                    <clickable-lot-no :obj="item" is-financial-item/>
                  </td>
                  <td>
                    <span v-if="item.lot && item.receipt && item.lot.inventory_uuid">
                      <linked-stock-no :stock-no="item.stock_no" :inventory-uuid="item.lot.inventory_uuid"
                                       :receipt-uuid="item.receipt.uuid"/>
                    </span>
                    <span v-else>
                      {{ item.stock_no }}
                    </span>
                  </td>
                  <td>{{ item.hammer_price | asCurrency(currency) }}</td>
                  <td>
                    <div style="width: 100px">
                      <currency-input-widget :currency="currency" size="sm" :model="item" attribute="hammer_adjustment"
                                             :label="false" @input="calculateData(item, 'hammer_adjustment')"/>
                    </div>
                  </td>
                  <td>
                    <div style="width: 100px">
                      <currency-input-widget :currency="currency" size="sm" :model="item" attribute="commission"
                                             :label="false" @input="calculateData(item, 'commission')"/>
                    </div>
                  </td>
                  <td>
                    <span v-as-status="item.status">{{ item.status | removeUnderscores | toUpperCaseFirst }}</span>
                  </td>
                  <td>
                    <span v-as-status="item.invoice_status">
                      {{ item.invoice_status || $t("N/A") | removeUnderscores | toUpperCaseFirst }}
                    </span>
                  </td>
                  <td>
                    <div v-if="item.reserve">
                      {{ item.reserve | asCurrency(currency) }}
                    </div>
                    <div v-if="item.reserve_type">
                      {{ item.reserve_type | removeUnderscores | toUpperCaseEveryWord }}
                    </div>
                  </td>
                  <td>
                    <input-widget :model="item" attribute="on_hold" type="checkbox" size="lg" :label="false"/>
                  </td>
                  <td class="text-center">
                    <b-tooltip :target="`move-to-settled-btn-${index}`" :title="$t('Move To Settled')"/>
                    <b-button size="sm" variant="primary" :id="`move-to-settled-btn-${index}`"
                              @click="onMoveToUnsettledClick(item)">
                      <font-awesome-icon icon="arrow-up"/>
                    </b-button>
                  </td>
                </tr>
              </template>
              <tr v-else>
                <td colspan="10">
                  <no-data-available :height="3"/>
                </td>
              </tr>
            </table>
          </div>
        </div>

        <div class="row">
          <div class="col-md-4 d-flex">
            <h5>{{ $t('Shipping') }}</h5>
            <input-widget @change="onShippingCheckboxChange" :model="model" attribute="enable_shipping" type="checkbox"
                          is-switch
                          :label="false" class="ml-3"/>
          </div>
          <div class="col-md-12">
            <table class="info-table table table-sm lots-table">
              <tr>
                <th style="width: 200px">{{ $t('Amount') }}</th>
                <th style="width: 200px">{{ $t('Tracking Code') }}</th>
                <th style="width: 30%">{{ $t('Taxable') }}</th>
                <th></th>
              </tr>
              <tr>
                <td class="pr-4">
                  <currency-input-widget :currency="currency" :model="model" @input="calculateData()"
                                         attribute="shipping_amount" type="number" :label="false"
                                         :disabled="!model.enable_shipping"/>
                </td>
                <td class="pr-4">
                  <input-widget :disabled="!model.enable_shipping" :model="model" attribute="shipping_tracking_code"
                                :label="false"/>
                </td>
                <td>
                  <input-widget :model="model" attribute="shipping_taxable" type="checkbox" @change="calculateData()"
                                :label="false" :disabled="!model.enable_shipping"/>
                </td>
                <td></td>
              </tr>
            </table>
          </div>
        </div>

        <div class="row" v-if="model.settlement_charges.length > 0">
          <div class="col-md-12">
            <h5>{{ $t('Charges') }}</h5>
          </div>
          <div class="col-md-12">
            <table class="info-table table table-sm lots-table">
              <tr>
                <th style="width: 15%">{{ $t('Description') }}</th>
                <th style="width: 15%">{{ $t('Amount') }}</th>
                <th style="width: 15%">{{ isSalesTaxEnabled ? $t('Sales Tax') : $t('VAT') }}</th>
                <th style="width: 15%">{{ $t('Taxable') }}</th>
              </tr>
              <template>
                <tr :key="`charge-item-${item.uuid}`" v-for="item in model.settlement_charges">
                  <td>
                    <template v-if="item.charge">
                      {{ item.charge.name }}
                    </template>
                  </td>
                  <td>
                    <div style="width: 100px">
                      <currency-input-widget :currency="currency" :model="item" attribute="amount" :label="false"
                                             size="sm"/>
                    </div>
                  </td>
                  <td>{{ item.vat | asCurrency(currency) }}</td>
                  <td>
                    <input-widget :model="item" attribute="taxable" :label="false" type="checkbox"/>
                  </td>
                </tr>
              </template>
            </table>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12 d-flex justify-content-between">
            <div class="d-flex align-items-center mb-2">
              <h5 class="mb-0">{{ $t('Vendor Charges') }}</h5>
            </div>
            <b-button size="sm" variant="primary" class="mb-2" @click="onAddChargeClick()">
              <font-awesome-icon icon="plus"/>
              {{ $t('Add Charge') }}
            </b-button>
          </div>
          <div class="col-md-12">
            <table class="info-table table table-sm lots-table">
              <thead>
              <tr>
                <th style="width: 20%">{{ $t('Name') }}</th>
                <th style="width: 20%">{{ $t('Comment') }}</th>
                <th style="width: 15%">{{ $t('Amount') }}</th>
                <th style="width: 15%">{{ isSalesTaxEnabled ? $t('Sales Tax') : $t('VAT') }}</th>
                <th style="width: 20%">{{ $t('Applies To') }}</th>
                <th class="actions-col text-center">{{ $t('Actions') }}</th>
              </tr>
              </thead>
              <tbody>
              <template v-if="existingAndUnsavedCharges.length > 0">
                <tr :key="`vendor-charge-item-${item.uuid || index}`"
                    v-for="(item, index) in existingAndUnsavedCharges.filter(item => item.amount !== 0)">
                    <td>{{ item.description }}</td>
                    <td>{{ item.comment }}</td>
                    <td>{{ item.amount | asCurrency(currency) }}</td>
                    <td>{{ item.vat | asCurrency(currency) }}</td>
                    <td>
                      <span v-if="item.is_func && (!item.lot || item.is_grouped)"></span>
                      <span v-else-if="item.lot">{{ $t('Lot') }}: {{ item.lot.lot_no }}</span>
                      <span v-else-if="item.inventory">{{ $t('Inventory') }}: {{ item.inventory.item_no }}</span>
                      <span v-else-if="item.receipt">{{ $t('Receipt') }}: {{ item.receipt.receipt_no }}</span>
                    </td>
                    <td class="text-center">
                      <b-tooltip :target="`delete-btn-${index}`" :title="$t('Delete')"/>
                      <b-button size="sm" variant="danger" :id="`delete-btn-${index}`"
                                @click="onDeleteChargeClick(item)">
                        <font-awesome-icon icon="minus"/>
                      </b-button>
                    </td>
                  </tr>
                </template>
                <tr v-else>
                  <td colspan="6">
                    <no-data-available :height="3"/>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-md-4 d-flex">
            <h5>{{ $t('Comment') }}</h5>
          </div>
          <div class="col-md-12">
            <input-widget :model="model" attribute="comment" type="textarea" label=""/>
          </div>
        </div>

      </div>
      <add-charge-modal/>

      <div class="row mt-2">
        <div class="col-md-5 offset-7">
          <settlement-totals-table :model="model" :financial-items="model.financial_items"
                                :charges="existingAndUnsavedCharges" :currency="currency"/>
        </div>
      </div>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "@/core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import SettlementModel from "@/modules/settlements/Settlement.model";
  import NoDataAvailable from "@/core/components/common/NoDataAvailable";
  import SettlementService from "@/modules/settlements/SettlementService";
  import {SOURCE_SETTLEMENTS_TABLE, SOURCE_VENDORS_TABLE} from "@/modules/constants";
  import CurrencyInputWidget from "@/core/widgets/CurrencyInputWidget";
  import _ from 'lodash';
  import FinancialItemModel from "@/modules/invoices/models/FinancialItem.model";
  import LanguageCodeSelectInputWidget from "@/core/widgets/LanguageCodeSelectInputWidget";
  import CustomerSelectInputWidget from "../../core/widgets/CustomerSelectInputWidget";
  import SaleSelectInputWidget from "../../core/widgets/SaleSelectInputWidget";
  import LotSelectInputWidget from "../../core/widgets/LotSelectInputWidget";
  import CustomerEmailAddressInfo from "../../core/components/customer-info/CustomerEmailAddressInfo";
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import LinkedStockNo from "@/modules/inventory/LinkedStockNo";
  import AddChargeModal from "./AddChargeModal";
  import AddChargeModel from "./AddCharge.model.js";
  import ClickableLotNo from "@/modules/lots/clickable-lot-no/ClickableLotNo";
  import {SettlementModalMixin} from "@/modules/settlements/settlementModal.mixin";
  import uuidV4 from 'uuid';
  import SettlementTotalsTable from "@/modules/settlements/SettlementTotalsTable.vue";

  const {mapState, mapActions} = createNamespacedHelpers('settlements');
  const {mapState: globalMapState, mapActions: globalMapActions} = createNamespacedHelpers('global');

  export default {
    name: "SettlementModal",
    mixins: [SettlementModalMixin],
    components: {
      SettlementTotalsTable,
      ClickableLotNo,
      LinkedStockNo,
      InputWidget,
      SaleSelectInputWidget,
      CustomerSelectInputWidget,
      LanguageCodeSelectInputWidget,
      CustomerEmailAddressInfo,
      CurrencyInputWidget,
      NoDataAvailable,
      ArtisioModal,
      LotSelectInputWidget,
      AddChargeModal
    },
    data() {
      return {
        modalUuid: uuidV4(),
        loading: false,
        showConfirmation: true,
        model: new SettlementModel(),
        selectedItems: new Set(),
        isSelectedAll: false,
        selectedSettledItems: new Set(),
        isAllSettledSelected: false,
        isRecalculating: false,
        currentCustomer: null,
        currentSale: null,
        hideSaleInput: false,
        onHoldInitial: {}
      }
    },
    watch: {
      async customer() {
        this.$nextTick(function () {
          if (this.customer) {
            this.model.customer_uuid = this.customer.uuid;
            this.model.customer = this.customer;
            this.customerUuid = this.customer.uuid
            if (!this.model.uuid) {
              this.model.settlement_billing_address = this.model.customer.billing_address
            }
          }
        })
      },
      editObject() {
        if (this.editObject) {
          this.currentSale = this.modal.sale;
          this.model.auction_uuid = this.currentSale.uuid;
          this.model = new SettlementModel(this.editObject);
          if (this.modal && this.modal.customer && !this.editObject.uuid) {
            this.model.preferred_language_code = this.modal.customer.preferred_language_code
            this.model.settlement_billing_address = this.modal.customer.billing_address
          }
          const items = [...this.model.financial_items, ...this.model.unsettled_financial_items]
          items.forEach(item => {
            this.onHoldInitial[item.uuid] = item.on_hold
          })

          this.setDefaultAddresses(
            this.editObject.settlement_billing_address,
            null
          )
        }
      },
    },
    computed: {
      ...globalMapState({
        hasMoreThanOneLanguage: state => state.hasMoreThanOneLanguage,
        enableContinuousAuction: state => state.enableContinuousAuction,
        isSalesTaxEnabled: state => state.isSalesTaxEnabled,
        showSettlementExtraOptions: state => state.showSettlementExtraOptions,
      }),
      ...mapState({
        modal: state => state.modal,
        disableCustomerInput: state => state.modal.disableCustomerInput,
        modalLoading: state => state.modal.loading,
        sale: state => state.modal.sale,
        tableData: state => state.tableData,
        customer: state => state.modal.customer,
        editObject: state => state.modal.object
      }),
      modalTitle() {
        if (this.modal.debitNoteWithoutRefundParams) {
          return this.$i18n.t('Recreate Settlement {no}', {no: this.model.settlement_no})
        }
        if (this.model.uuid) {
          return this.$i18n.t('Update Settlement');
        }
        if (this.modal && this.modal.customer) {
          return this.$i18n.t('Create Settlement for {display_name}', {display_name: this.modal.customer.display_name});
        }
        return this.$i18n.t('Create Settlement');
      },
      currency() {
        if (this.currentSale && this.currentSale.currency) {
          return this.currentSale.currency.symbol;
        }
        return null;
      },
      disabled() {
        return !!this.model.uuid;
      },
      existingAndUnsavedCharges() {
        return [...this.model.miscellaneous_items, ...this.modal.unsavedCharges];
      }
    },
    methods: {
      ...mapActions([
        'hideModal',
        'getTableData',
        'getModalData',
        'updateTableSource',
        'resetSearchModel',
        'updateSearchModel',
        'showAddChargeModal',
      ]),
      ...globalMapActions(['showAddressModal']),
      async onShow() {
        if (this.sale) {
          this.hideSaleInput = true;
          this.currentSale = _.cloneDeep(this.sale);
          this.model.auction_uuid = this.currentSale.uuid;
        }
        if (this.customer) {
          this.model.customer_uuid = this.customer.uuid;
          this.model.customer = this.customer;
          this.currentCustomer = this.customer;
        }
        this.showConfirmation = true;

        if (this.sale && this.customer) {
          await this.getInfo()
        }
      },
      onHidden() {
        this.customerUuid = null
        this.hideModal();
        this.clearData();
        this.currentCustomer = null;
        this.currentSale = null;
        this.hideSaleInput = false;
        this.model = new SettlementModel();
        this.onHoldInitial = {};
      },
      async onMoveToSettledClick(item) {
        this.model.financial_items.push(_.cloneDeep(item));
        this.$nextTick(function () {
          this.model.unsettled_financial_items = this.model.unsettled_financial_items.filter(i => item.uuid !== i.uuid)
          this.isSelectedAll = this.selectedItems.size === this.model.unsettled_financial_items.length;
          if (!this.model.unsettled_financial_items.length) {
            this.isSelectedAll = false;
          }
        })
        this.selectedItems.delete(item)
        this.isAllSettledSelected = this.selectedSettledItems.size === this.model.financial_items.length;
        await this.updateMiscItemsAndUnsavedCharges();
        await this.calculateData();
      },
      async onMoveToUnsettledClick(item) {
        this.model.unsettled_financial_items.push(_.cloneDeep(item));
        this.$nextTick(async () => {
          this.model.financial_items = this.model.financial_items.filter(i => item.uuid !== i.uuid)
          this.isAllSettledSelected = this.selectedSettledItems.size === this.model.financial_items.length;
          if (!this.model.financial_items.length) {
            this.isAllSettledSelected = false;
          }
          await this.updateMiscItemsAndUnsavedCharges(true);
        });
        this.selectedSettledItems.delete(item)
        this.isSelectedAll = this.selectedItems.size === this.model.unsettled_financial_items.length;
      },
      async moveSelectedToSettled() {
        let selectedItemsArray = [...this.selectedItems];
        selectedItemsArray.map(i => {
          this.model.financial_items.push(_.cloneDeep(i));
          this.$nextTick(() => {
            this.model.unsettled_financial_items = this.model.unsettled_financial_items.filter(u => i.uuid !== u.uuid);
          });
        });
        this.selectedItems = new Set();
        this.isSelectedAll = this.selectedItems.size === this.model.unsettled_financial_items.length;
        this.isAllSettledSelected = this.selectedSettledItems.size === this.model.financial_items.length;
        await this.updateMiscItemsAndUnsavedCharges();
        await this.calculateData();
      },
      async moveSelectedToUnsettled() {
        let selectedSettledItemsArray = [...this.selectedSettledItems];
        selectedSettledItemsArray.map(i => {
          this.model.unsettled_financial_items.push(_.cloneDeep(i));
          this.$nextTick(() => this.model.financial_items = this.model.financial_items.filter(u => i.uuid !== u.uuid))
        });
        this.selectedSettledItems = new Set();
        this.isAllSettledSelected = this.selectedSettledItems.size === this.model.financial_items.length;
        this.isSelectedAll = this.selectedItems.size === this.model.unsettled_financial_items.length;
        this.$nextTick(async () => {
          await this.updateMiscItemsAndUnsavedCharges(true);
        });
      },
      async updateMiscItemsAndUnsavedCharges(isDelete) {
        this.loading = true;
        let {success, body} = await SettlementService.calculateMultipleVendorCharges({
          financial_items_uuids: this.model.financial_items.map(item => item.uuid),
          existing_misc_items_signatures: this.model.miscellaneous_items.map(i => this.calculateChargeSignature(i)),
          settlement_uuid: this.model.uuid,
          customer_uuid: this.model.customer_uuid,
          auction_uuid: this.model.auction_uuid,
          is_delete: isDelete,
          unsaved_func_charges_uuids: this.modal.unsavedCharges.filter(i => i.is_func).map(i => i.vendor_charge_uuid)
        });
        this.loading = false;
        if (!success) {
          await this.$errorDialog(this.$i18n.t('Unable to fetch vendor charges for selected items'));
          return;
        }
        let receivedCharges = body.unsaved_charges.map(i => new AddChargeModel(i));
        this.model.miscellaneous_items = this.model.miscellaneous_items.filter(i => !i.vendor_charge_uuid || body.misc_items.includes(this.calculateChargeSignature(i)));
        if (isDelete) {
          let receivedChargesSignatures = receivedCharges.map(i => this.calculateChargeSignature(i));
          let remainingUnsavedCharges = this.modal.unsavedCharges.filter(i => !i.vendor_charge_uuid || !i.is_func && receivedChargesSignatures.includes(this.calculateChargeSignature(i)));
          this.modal.unsavedCharges = [...remainingUnsavedCharges, ...receivedCharges.filter(i => i.is_func)];
        } else {
          this.modal.unsavedCharges = [...this.modal.unsavedCharges.filter(i => !i.vendor_charge_uuid), ...receivedCharges];
        }
      },
      onRowSelect(checked, item) {
        if (checked) {
          this.selectedItems.add(item)
        } else {
          this.selectedItems.delete(item)
        }
        this.isSelectedAll = this.selectedItems.size === this.model.unsettled_financial_items.length;
        this.selectedItems = new Set(this.selectedItems);
      },
      selectAllRows(checked) {
        this.clearSelected();
        this.isSelectedAll = checked;

        if (this.isSelectedAll) {
          this.model.unsettled_financial_items.forEach(item => this.selectedItems.add(item));
        }
        this.selectedItems = new Set(this.selectedItems);
      },
      clearSelected() {
        this.selectedItems.clear();
        this.selectedItems = new Set([...this.selectedItems]);
        this.isSelectedAll = false;
      },
      onSettledRowSelect(checked, item) {
        if (checked) {
          this.selectedSettledItems.add(item)
        } else {
          this.selectedSettledItems.delete(item)
        }
        this.isAllSettledSelected = this.selectedSettledItems.size === this.model.financial_items.length;
        this.selectedSettledItems = new Set(this.selectedSettledItems);
      },
      selectAllSettledRows(checked) {
        this.clearSettledSelected();
        this.isAllSettledSelected = checked;

        if (this.isAllSettledSelected) {
          this.model.financial_items.forEach(item => this.selectedSettledItems.add(item));
        }
        this.selectedSettledItems = new Set(this.selectedSettledItems);
      },
      clearSettledSelected() {
        this.selectedSettledItems.clear();
        this.selectedSettledItems = new Set([...this.selectedSettledItems]);
        this.isAllSettledSelected = false;
      },
      async onSubmit() {
        if (this.model.is_not_editable) {
          this.$errorDialog(this.$i18n.t('You can not edit legacy settlement'));
          return;
        }
        const items = [...this.model.financial_items, ...this.model.unsettled_financial_items]
        for (let item of items) {
          if (item.on_hold && item.on_hold !== this.onHoldInitial[item.uuid] && item.introductory_settlement_status === 'paid') {
            const confirm = await this.$confirm(this.$t('The associated {document_type} is Paid. Do you want to mark Item as On Hold?', {document_type: this.$t('Auction Introductory Settlement')}))
            if (confirm) {
              break
            } else {
              return
            }
          }
        }

        let extra_info = {}
        this.showSettlementExtraOptions.forEach(i => extra_info[i.key] = this.model[i.key])

        this.loading = true;
        let response;
        let requestData = {
          uuid: this.model.uuid,
          customer_uuid: this.model.customer_uuid,
          auction_uuid: this.model.auction_uuid,
          miscellaneous_item_uuids: this.model.miscellaneous_items.map(item => item.uuid),
          financial_items: this.model.financial_items.map(item => _.omit(item.toJSON(), ['lot', 'receipt'])),
          unsettled_financial_items: this.model.unsettled_financial_items.map(item => _.omit(item.toJSON(), ['lot', 'receipt'])),
          preferred_language_code: this.model.preferred_language_code,
          additional_page: this.model.additional_page,
          // settlement_charges: this.model.settlement_charges,
          is_exported: this.model.is_exported,
          shipping_amount: this.model.shipping_amount,
          shipping_vat: this.model.shipping_vat,
          shipping_taxable: this.model.shipping_amount ? this.model.shipping_taxable : false,
          shipping_tracking_code: this.model.shipping_amount ? this.model.shipping_tracking_code : null,
          date: this.model.date,
          unsaved_charges: this.modal.unsavedCharges.map(charge => {return {
            amount: charge.amount,
            description: charge.description,
            inventory_uuid: charge.inventory ? charge.inventory.uuid : null,
            lot_uuid: charge.lot ? charge.lot.uuid : null,
            vat: charge.vat,
            vendor_charge_uuid: charge.vendor_charge_uuid,
            receipt_uuid: charge.receipt ? charge.receipt.uuid : null,
            comment: charge.comment
          }}),
          extra_info,
          comment: this.model.comment,
          settlement_billing_address: this.model.settlement_billing_address
        };

        if (this.modal.debitNoteWithoutRefundParams) {
          requestData.inherited_from_uuid = requestData.uuid;
          requestData.uuid = null;
          requestData.debit_note_without_refund_params = this.modal.debitNoteWithoutRefundParams;
          requestData.unsaved_charges.push(...this.model.miscellaneous_items.map(item => {
            return {
              amount: item.amount,
              description: item.description,
              inventory_uuid: item.inventory_uuid,
              lot_uuid: item.lot_uuid,
              receipt_uuid: item.receipt_uuid,
              vat: item.vat,
              vendor_charge_uuid: item.vendor_charge_uuid,
              comment: item.comment
            }
          }));
          response = await SettlementService.createSettlement(requestData);
        } else if (!requestData.uuid) {
          response = await SettlementService.createSettlement(requestData);
        } else {
          response = await SettlementService.updateSettlement(requestData.uuid, requestData);
        }

        this.loading = false;

        if (response.success) {
          this.showConfirmation = false;
          if (this.modal.source === SOURCE_SETTLEMENTS_TABLE) {
            this.getTableData();
            this.hideModal();
          } else if (this.modal.source === SOURCE_VENDORS_TABLE) {
            const tmpCustomer = _.cloneDeep(this.customer)
            await this.updateTableSource({source: 'sale'})
            this.resetSearchModel()
            this.updateSearchModel({searchModel: {
                customer_uuid: tmpCustomer.uuid,
                customer: tmpCustomer
              }})
            this.$router.push({name: "sales.settlements"});
            this.hideModal();
          }
        } else if (response.body.settlement_addresses) {
          this.$errorDialog(this.$i18n.t("Some required fields are missing in the settlement billing address"));
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          }
        }
      },
      calculateData: _.debounce(async function (item, fieldName) {
        this.loading = true;
        this.isRecalculating = true;

        let financial_items = []
        let lot_uuids = []
        if (item) {
          financial_items = [
            {
              uuid: item.uuid,
              commission: fieldName === 'commission' ? parseFloat(item.commission) : null,
              hammer_adjustment: parseFloat(item.hammer_adjustment)
            }
          ];
          if (item.lot){
            lot_uuids.push(item.lot.uuid)
          }
        } else {
          this.model.financial_items.forEach(fi => {
              financial_items.push({
                uuid: fi.uuid,
                commission: null,
                hammer_adjustment: parseFloat(fi.hammer_adjustment)
              });
              if (fi.lot){
                lot_uuids.push(fi.lot.uuid)
              }
          })
        }

        const remaining_unsaved_charges = this.modal.unsavedCharges.filter(charge => charge.is_func || !charge.lot || !lot_uuids.includes(charge.lot.uuid));

        const unsaved_charges = this.modal.unsavedCharges.filter(charge => !charge.is_func && charge.lot).reduce((acc, charge) => {
          const lotUuid = charge.lot.uuid;
          if (!acc[lotUuid]) {
            acc[lotUuid] = [];
          }
          acc[lotUuid].push(charge.vendor_charge_uuid);
          return acc;
        }, {});

        let extra_info = {}
        this.showSettlementExtraOptions.forEach(i => extra_info[i.key] = this.model[i.key])

        let {success, body} = await SettlementService.calculateData({
          financial_items,
          unsaved_charges,
          is_exported: this.model.is_exported,
          extra_info,
          settlement_billing_address: this.model.settlement_billing_address,
          uuid: this.model.uuid,
          customer_uuid: this.model.customer_uuid,
          type: 'auction',
          shipping_amount: this.model.enable_shipping ? this.model.shipping_amount : null,
          shipping_taxable: this.model.enable_shipping ? this.model.shipping_taxable : false,
          sale_no: this.sale.sale_no
        }, '');

        if (success) {
          if (body.financial_items.length > 0) {
            for (let financialItem of body.financial_items) {
              let itemIndex = this.model.financial_items.findIndex(i => i.uuid === financialItem.uuid);
              if (itemIndex >= 0) {
                this.model.financial_items[itemIndex] = new FinancialItemModel({
                  ...this.model.financial_items[itemIndex],
                  ...financialItem
                });
                continue;
              }
              itemIndex = this.model.unsettled_financial_items.findIndex(i => i.uuid === financialItem.uuid);
              if (itemIndex >= 0) {
                this.model.unsettled_financial_items[itemIndex] = new FinancialItemModel({
                  ...this.model.unsettled_financial_items[itemIndex],
                  ...financialItem
                });
              }
            }
            this.model.financial_items = [...this.model.financial_items];
            this.model.unsettled_financial_items = [...this.model.unsettled_financial_items];
            for (let mi of this.model.miscellaneous_items) {
              if (body.misc_items[mi.uuid]) {
                mi.amount = body.misc_items[mi.uuid].amount;
                mi.vat = body.misc_items[mi.uuid].vat;
              }
            }
            this.modal.unsavedCharges = [...remaining_unsaved_charges, ...body.unsaved_charges];
            this.model.shipping_amount = body.shipping_amount
            this.model.shipping_vat = body.shipping_vat
          }
        }
        this.isRecalculating = false;
        this.loading = false;
      }, 500),
      onCustomerChange(customer) {
        this.currentCustomer = _.cloneDeep(customer)
        this.customerUuid = customer.uuid
        this.getInfo();
      },
      onSaleChange(sale) {
        this.currentSale = _.cloneDeep(sale)
        this.getInfo();
      },
      onLotChange(lot) {
        this.currentSale = _.cloneDeep(lot.auction);
        this.model.auction_uuid = lot.auction_uuid;
        if (this.enableContinuousAuction && !this.currentCustomer) {
          this.model.customer = lot.inventory.customer;
          this.model.customer_uuid = this.model.customer.uuid;
        }
        this.getInfo();
      },
      async getInfo() {
        this.model.resetErrors();

        if (!this.model.customer_uuid || !this.model.auction_uuid) {
          return;
        }

        let params = {
          customer_uuid: this.model.customer_uuid,
          auction_uuid: this.model.auction_uuid
        };

        const {success, body} = await this.getModalData({
          params,
          object: this.model.toJSON(),
          sale: this.currentSale,
          customer: this.currentCustomer,
        });
        if (!success) {
          if (body.non_field_errors) {
            this.$errorDialog(body.non_field_errors[0]);
          } else {
            if (this.enableContinuousAuction && body.auction_uuid) {
              body.lot_uuid = body.auction_uuid;
            }
            this.model.setMultipleErrors(body);
          }
        }
      },
      clearSaleInput() {
        this.currentSale = null;
        this.clearData();
        this.model.resetErrors();
      },
      clearCustomerInput() {
        this.currentCustomer = null;
        this.clearData();
        this.model.resetErrors();
      },
      clearLotInput() {
        this.currentSale = null;
        this.model.auction_uuid = null;
        this.clearData();
        this.model.resetErrors();
      },
      clearData() {
        this.model.financial_items = [];
        this.model.unsettled_financial_items = [];
        this.model.miscellaneous_items = [];
        this.isSelectedAll = false;
        this.selectedItems = new Set();
        this.isAllSettledSelected = false;
        this.selectedSettledItems = new Set();
        this.model.enable_shipping = false;
        this.model.shipping_amount = null;
        this.model.shipping_vat = null;
        this.model.shipping_taxable = null;
        this.model.shipping_tracking_code = null;
        this.modal.unsavedCharges = [];
      },
      onShippingCheckboxChange() {
        this.model.shipping_tracking_code = null;
        this.model.shipping_amount = null;
        this.model.shipping_vat = null;
        this.model.shipping_taxable = null;
      },
      onAddChargeClick() {
        this.showAddChargeModal({
          settlement: this.model,
          unsavedCharges: this.modal.unsavedCharges,
          settlementModal: this.modal,
          funcParams: {
            financial_items_uuids: this.model.financial_items.map(i => i.uuid),
            existing_misc_items_signatures: this.model.miscellaneous_items.map(i => this.calculateChargeSignature(i)),
            unsaved_charges_signatures: this.modal.unsavedCharges.map(i => {
              if (i.is_func) {
                return i.vendor_charge_uuid;
              }
              return this.calculateChargeSignature(i);
            })
          }
        });
      },
      onDeleteChargeClick(item) {
        const indexInModel = this.model.miscellaneous_items.findIndex(c => c.uuid === item.uuid);
        if (indexInModel !== -1) {
          this.model.miscellaneous_items.splice(indexInModel, 1);
          return;
        }

        const indexInUnsavedCharges = this.modal.unsavedCharges.findIndex(c => c === item);
        if (indexInUnsavedCharges !== -1) {
          this.modal.unsavedCharges.splice(indexInUnsavedCharges, 1);
        }
      },
      calculateChargeSignature(vc) {
        let itemUuid = '';
        if (vc.lot) {
          itemUuid = vc.lot.uuid;
        } else if (vc.inventory) {
          itemUuid = vc.inventory.uuid;
        } else if (vc.receipt) {
          itemUuid = vc.receipt.uuid;
        }
        return `${vc.vendor_charge_uuid}${itemUuid}`;
      }
    }
  }
</script>

<style lang="scss" scoped>
  .settlement-wrapper {
    white-space: nowrap;
  }

  .unsettled-items-table, .settled-items-table {
    .lot-col {
    }

    .receipt-col {
    }

    .hammer-price-col {
      white-space: break-spaces;
    }

    .hammer-adjustment-col {
      white-space: break-spaces;
    }

    .commission-col {

    }

    .status-col {

    }

    .invoice-col {

    }

    .on-hold-col {

    }

    .actions-col {

    }
  }

</style>
